import { CommonModule } from '@angular/common';
import { Component, inject, Optional } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { forkJoin, map, of, catchError, switchMap } from 'rxjs';
import { HttpClient, HttpContext } from '@angular/common/http';
import { backendEndPointToken, serviceRequestCategoriesValue } from '../../app.config';
import { DynamicFormsV2Component } from '../../shared/dynamic-forms-v2/dynamic-forms-v2.component';
import { ErrorService } from '../../shared/error.service';
import { StateService } from '../../shared/state.service';
import { AuthService } from '../../shared/auth/auth.service';
import { FhirResourceService } from '../../shared/fhir-resource.service';
import { UtilityService } from '../../shared/utility.service';
import { formMetaData, FormFields, GroupField, IndividualField, SingleCodeField, CodeableConceptField, IndividualReferenceField } from '../../shared/dynamic-forms.interface2';
import { Bundle, ChargeItem, ChargeItemDefinition, Invoice, ServiceRequest } from 'fhir/r4';
import { LoadingUIEnabled } from '../../loading.interceptor';

type AddServiceRequestDialogData = {
    patientId?: string | null;
    encounterId?: string | null;
};

@Component({
    selector: 'app-add-service-request',
    standalone: true,
    imports: [CommonModule, MatCardModule, MatButtonModule, MatIconModule, DynamicFormsV2Component],
    templateUrl: './add-service-request.component.html',
    styleUrls: ['./add-service-request.component.scss']
})
export class AddServiceRequestComponent {
    private http = inject(HttpClient);
    private backendEndPoint = inject(backendEndPointToken);
    private errorService = inject(ErrorService);
    private stateService = inject(StateService);
    private auth = inject(AuthService);
    private fhirResourceService = inject(FhirResourceService);
    private util = inject(UtilityService);

    private dref = inject(MatDialogRef<AddServiceRequestComponent>, { optional: true });
    data = inject(MAT_DIALOG_DATA, { optional: true }) as AddServiceRequestDialogData | null;

    // context (may be missing -> allowed)
    patientId = this.data?.patientId ?? this.stateService.currentPatientIdFromResolver?.getValue?.() ?? this.stateService.currentEncounter?.getValue?.()?.patientId ?? null;
    encounterId = this.data?.encounterId ?? this.stateService.currentEncounter?.getValue?.()?.id ?? null;

    practitionerId = this.auth.user?.getValue?.()?.['fhirResourceId'] ?? null;

    formMeta: formMetaData = {
        formName: 'Add Service Request',
        formDescription: 'Create a service request. Patient is optional.',
        submitText: 'Submit',
        closeDialogOnSubmit: true
    };

    // fetched options
    serviceRequestCodeOptions: any[] = [];
    chargeItemDefinitions: ChargeItemDefinition[] = [];

    formFields: FormFields[] = [];

    ngOnInit(): void {
        // For now: reuse your existing “service request codes list” data source if present via FormFieldsSelectDataService;
        // but since you asked to be self-contained, fetch:
        // - ChargeItemDefinition (for billing)
        // - ServiceRequest codes from your existing code system list endpoint (if your select-data service already wraps this, swap later).
        forkJoin({
            chargeDefs: this.http.get<any>(`${this.backendEndPoint}/ChargeItemDefinition`).pipe(
                map((b: any) => (b?.entry ?? []).map((e: any) => e.resource).filter(Boolean)),
                catchError(() => of([]))
            ),
            // NOTE: replace this with your existing endpoint used by add-lab-requests/service-request-codes list if different.
            // Keeping a safe fallback so UI still works.
            codes: this.http.get<any>(`${this.backendEndPoint}/ValueSet/service-request-codes/$expand`).pipe(
                map((vs: any) => (vs?.expansion?.contains ?? []).map((c: any) => `${c.code}$#$${c.display}$#$${c.system}`)),
                catchError(() => of([]))
            )
        }).subscribe(({ chargeDefs, codes }) => {
            this.chargeItemDefinitions = chargeDefs;
            this.serviceRequestCodeOptions = codes;

            this.buildFormFields();
        });
    }

    private buildFormFields(): void {
        const patientRef = this.patientId ? { reference: `Patient/${this.patientId}` } : undefined;
        const encounterRef = this.encounterId ? { reference: `Encounter/${this.encounterId}` } : undefined;
        const requesterRef = this.practitionerId ? { reference: `Practitioner/${this.practitionerId}` } : undefined;

        // categories from app.config.ts (hardcoded list)
        const categories = (serviceRequestCategoriesValue || []).map(c => `${c.code}$#$${c.display}$#$${c.system}`);

        this.formFields = <FormFields[]>[
            <SingleCodeField>{
                generalProperties: {
                    fieldApiName: 'status',
                    fieldName: 'Status',
                    fieldLabel: 'Status',
                    fieldType: 'SingleCodeField',
                    isArray: false,
                    isGroup: false,
                    value: 'active',
                    isHidden: true
                },
                data: ['active']
            },

            <CodeableConceptField>{
                generalProperties: {
                    fieldApiName: 'category',
                    fieldName: 'Category',
                    fieldLabel: 'Category',
                    fieldType: 'CodeableConceptField',
                    isArray: true,
                    isGroup: false,
                    validations: [{ type: 'default', name: 'required', isFunction: false }]
                },
                data: categories
            },

            <CodeableConceptField>{
                generalProperties: {
                    fieldApiName: 'code',
                    fieldName: 'Service Code',
                    fieldLabel: 'Service Code',
                    fieldType: 'CodeableConceptFieldFromBackEnd',
                    isArray: false,
                    isGroup: false,
                    validations: [{ type: 'default', name: 'required', isFunction: false }]
                },
                // If your DynamicFormsV2 treats data[0] as URL for backend search, replace accordingly.
                // Here we pass a static list as fallback.
                data: this.serviceRequestCodeOptions
            },

            <IndividualReferenceField>{
                generalProperties: {
                    fieldApiName: 'subject',
                    fieldName: 'Patient (optional)',
                    fieldLabel: 'Patient (optional)',
                    fieldType: 'IndividualReferenceField',
                    isArray: false,
                    isGroup: false,
                    isHidden: !!patientRef,
                    value: patientRef as any
                },
                data: [] // your select-data service usually provides Patient refs; wire later
            },

            <IndividualReferenceField>{
                generalProperties: {
                    fieldApiName: 'encounter',
                    fieldName: 'Encounter (optional)',
                    fieldLabel: 'Encounter (optional)',
                    fieldType: 'IndividualReferenceField',
                    isArray: false,
                    isGroup: false,
                    isHidden: !!encounterRef,
                    value: encounterRef as any
                },
                data: []
            },

            <IndividualReferenceField>{
                generalProperties: {
                    fieldApiName: 'requester',
                    fieldName: 'Requester',
                    fieldLabel: 'Requester',
                    fieldType: 'IndividualReferenceField',
                    isArray: false,
                    isGroup: false,
                    isHidden: !!requesterRef,
                    value: requesterRef as any
                },
                data: []
            },

            <IndividualField>{
                generalProperties: {
                    fieldApiName: 'note',
                    fieldName: 'Clinical Note',
                    fieldLabel: 'Clinical Note',
                    inputType: 'textarea',
                    isArray: false,
                    isGroup: false
                }
            },

            // Occurrence (simple; you can later switch to group with period/timing based on category)
            <IndividualField>{
                generalProperties: {
                    fieldApiName: 'occurrenceDateTime',
                    fieldName: 'Scheduled Date/Time (optional)',
                    fieldLabel: 'Scheduled Date/Time (optional)',
                    inputType: 'datetime-local',
                    isArray: false,
                    isGroup: false
                }
            }
        ];
    }

    onSubmitted(evt: any): void {
        const v = evt?.values ?? evt;
        if (!v) return;

        // Patient optional; but code + category required
        if (!v.code) {
            this.errorService.openandCloseError('Service code is required.');
            return;
        }
        if (!v.category || (Array.isArray(v.category) && v.category.length === 0)) {
            this.errorService.openandCloseError('Category is required.');
            return;
        }

        const sr = this.buildServiceRequest(v);

        // Billing: find matching ChargeItemDefinition for chosen code/category (best effort)
        // You likely map ChargeItemDefinition.code / instance to the service; adjust mapping as your backend expects.
        const chargeDef = this.pickChargeItemDefinitionForService(v);

        const bundle = this.buildTransactionBundle(sr, chargeDef);

        // submit as transaction
        this.fhirResourceService.postBundle(bundle).subscribe({
            next: () => this.dref?.close(true),
            error: (err) => {
                console.error('Failed to submit service request bundle', err);
                this.errorService.openandCloseError('Failed to submit service request.');
            }
        });
    }

    private buildServiceRequest(v: any): ServiceRequest {
        const subjectRef = this.coerceReference(v.subject) ?? (this.patientId ? { reference: `Patient/${this.patientId}` } : undefined);
        const encounterRef = this.coerceReference(v.encounter) ?? (this.encounterId ? { reference: `Encounter/${this.encounterId}` } : undefined);
        const requesterRef = this.coerceReference(v.requester) ?? (this.practitionerId ? { reference: `Practitioner/${this.practitionerId}` } : undefined);

        const sr: any = {
            resourceType: 'ServiceRequest',
            status: 'active',
            intent: 'order',
            authoredOn: new Date().toISOString(),
            code: this.util['buildFhirResource'] ? undefined : undefined // keep lint quiet; we set below
        };

        // category is array
        sr.category = Array.isArray(v.category) ? v.category.map((x: any) => this.toCodeableConcept(x)).filter(Boolean) : [this.toCodeableConcept(v.category)].filter(Boolean);
        sr.code = this.toCodeableConcept(v.code);

        if (subjectRef?.reference) sr.subject = subjectRef;
        if (encounterRef?.reference) sr.encounter = encounterRef as any;
        if (requesterRef?.reference) sr.requester = requesterRef as any;

        if (v.note) sr.note = [{ text: String(v.note) }];

        if (v.occurrenceDateTime) {
            const d = new Date(v.occurrenceDateTime);
            if (!isNaN(d.getTime())) sr.occurrenceDateTime = d.toISOString();
        }

        return sr as ServiceRequest;
    }

    private pickChargeItemDefinitionForService(v: any): ChargeItemDefinition | null {
        // best-effort mapping; adjust once you confirm how your ChargeItemDefinition is keyed for ServiceRequest services.
        const codeStr = typeof v.code === 'string' ? v.code : '';
        const code = codeStr.includes('$#$') ? codeStr.split('$#$')[0] : '';
        const display = codeStr.includes('$#$') ? codeStr.split('$#$')[1] : '';

        const candidates = this.chargeItemDefinitions || [];
        const found =
            candidates.find(cd => (cd as any)?.code?.coding?.some((c: any) => c.code === code)) ||
            candidates.find(cd => (cd as any)?.code?.text?.toLowerCase?.() === display?.toLowerCase?.());

        return found ?? null;
    }

    private buildTransactionBundle(sr: ServiceRequest, chargeDef: ChargeItemDefinition | null): Bundle {
        // create ChargeItem + Invoice only if we can derive charge definition
        const entries: any[] = [];

        entries.push({
            resource: sr,
            request: { method: 'POST', url: 'ServiceRequest' }
        });

        if (chargeDef) {
            const subjectRef = (sr as any).subject;
            const encounterRef = (sr as any).encounter;
            const organizationId = this.auth.getOrganizationId();

            const chargeItem: ChargeItem = {
                resourceType: 'ChargeItem',
                status: 'billable',
                subject: subjectRef,
                context: encounterRef,
                code: chargeDef.code,
                quantity: { value: 1 },
                definitionCanonical: chargeDef.url ? [chargeDef.url] : undefined,
                ...(organizationId ? { performingOrganization: { reference: `Organization/${organizationId}` } } : {})
            } as any;

            entries.push({
                resource: chargeItem,
                request: { method: 'POST', url: 'ChargeItem' }
            });

            // Minimal Invoice (issued). Later: add lineItem linking to the ChargeItem response via conditional/urn:uuid, if you standardize that pattern.
            const invoice: Invoice = {
                resourceType: 'Invoice',
                status: 'issued',
                subject: subjectRef,
                date: new Date().toISOString()
            } as any;

            entries.push({
                resource: invoice,
                request: { method: 'POST', url: 'Invoice' }
            });
        }

        return { resourceType: 'Bundle', type: 'transaction', entry: entries } as Bundle;
    }

    private toCodeableConcept(raw: any): any {
        if (!raw) return undefined;
        if (typeof raw === 'string' && raw.includes('$#$')) {
            const [code = '', display = '', system = ''] = raw.split('$#$');
            return { coding: [{ code, display, system }], text: display || code };
        }
        if (typeof raw === 'string') return { text: raw };
        if (typeof raw === 'object' && (raw.coding || raw.text)) return raw;
        return { text: String(raw) };
    }

    private coerceReference(input: any): { reference: string; display?: string } | undefined {
        if (!input) return undefined;
        if (typeof input === 'string') {
            const parts = input.split('$#$');
            return { reference: `${parts[0]}`.trim(), display: parts[1]?.trim() };
        }
        if (typeof input === 'object' && input.reference) {
            return { reference: input.reference, display: input.display };
        }
        return undefined;
    }

    close(): void {
        this.dref?.close();
    }
}
